import axios from "axios";
import {message} from "antd";
import {API_URL} from "../constants";
import {authHeader, download} from "../Utilities";
import HierarchyNode from "../components/ImageViewerHandlers/Hierarchy/HierarchyNode";
import Hierarchy from "../components/ImageViewerHandlers/Hierarchy/Hierarchy";
import ObjectReference from "../components/ImageViewerHandlers/Hierarchy/ObjectReference";

import {TagReference} from "./ApiModels/TagReference";
import {SystemAttribute} from "../components/ImageViewerHandlers/ImageViewerTypes";
import {isString, StringWithAutocomplete} from "../UtilitiesTs";
import {ResultReference} from "./ApiModels/ResultReference";

type TagListItem = {
    text: string,
    label: string,
    full_load: boolean,
}

type SearchInHierarchyFilter = {
    toRequestBody?: () => any
}

export type HierarchyModel = {
    id: string,
    name: string,
    hidden: boolean;
    workspace_id: string,
    use_v2_references: boolean,
}

type NodeAttributeModel = {
    key: string,
    value: string
}

export type NodeLoadingInfoModel = {
    full: boolean,
    subnodes_count?: number
}

export type NodeReferenceModel = {
    id: string,
    page_id: string,
    x_rel: number,
    y_rel: number,
}

type NodeId = string;

type ExpandedSubtreeNode = {
    id: NodeId,
    text: string,
    label: string,
}

export type NodeModel = {
    attributes: NodeAttributeModel[],
    hierarchy_id: string,
    id: string,
    label: string,
    loading_info: NodeLoadingInfoModel
    parent_node_id?: string,
    references: NodeReferenceModel[],
    text: string,
    aliases: string[]
}

export type NodeUpdateModel = {
    attributes: NodeAttributeModel[],
    id: string,
    label: string,
    parent_node_id?: string,
    references: NodeReferenceModel[],
    text: string,
}

type FetchPartialHierarchyResponse = {
    nodes_list: NodeModel[],
    hierarchy: HierarchyModel
}

export type ReferenceExtraData = {
    id: string,
    tag_reference?: TagReference,
    file_name: string,
    page_number: number,
}

export type ReferenceExtraDataV2 = {
    tag_reference: TagReference,
    file_name: string,
    page_number: number,
}

export type HierarchySystemAttributes = {
    name: string,
    options: string[],
    label: string,
}

type AddMultipleNodeCommand = {
    type: "update" | "create",
    node: HierarchyNode
}

export type LogAuthor = {
    user: {id: string, name: string},
    date: Date
}

export type HierarchyLog = {
    id: string,
    created_by: LogAuthor,
    reverted_by?: LogAuthor,
    type: StringWithAutocomplete<"new-node" | "update-node" | "add-multiple-node" | "delete-node" | "bulk">,
}

export type HierarchyLogOperationNodeDumpRef = {
    id: string,
    page_id: string,
    x_rel: number,
    y_rel: number,
}

export type HierarchyLogOperationNodeDumpAttr = {
    key: string,
    value: string
}

export type HierarchyLogOperationNodeDump = {
    id: string,
    text: string,
    label: string,
    parent_id?: string,
    references: HierarchyLogOperationNodeDumpRef[],
    attributes: HierarchyLogOperationNodeDumpAttr[],
    aliases: string[],
}

export type SetterTextAndLabel = {
    type: "text_and_label",
    data: {
        text: string,
        label: string
    }
}

export type SetterAttributes = {
    type: "attributes",
    data: HierarchyLogOperationNodeDumpAttr[]
}

export type SetterReferences = {
    type: "references",
    data: HierarchyLogOperationNodeDumpRef[]
}

export type SetterParent = {
    type: "parent",
    data: string
}

export type HierarchyLogOperationSetter = SetterTextAndLabel | SetterAttributes | SetterReferences | SetterParent | {
    type: string,
    data: any
};

interface IHierarchyLogOperationSetterTyped<T> {
    value: T
}

export class HierarchyLogOperationSetterTyped {
    private readonly _setter: HierarchyLogOperationSetter;

    constructor(setter: HierarchyLogOperationSetter) {
        this._setter = setter;
    }

    get value(): {} {
        return this._setter;
    }

    isTextAndLabel(): this is IHierarchyLogOperationSetterTyped<SetterTextAndLabel> {
        return this._setter.type == "text_and_label";
    }
}

export type HierarchyLogOperation = {
    id: string,
    type: StringWithAutocomplete<"updated" | "created" | "deleted">,
    prev?: HierarchyLogOperationNodeDump,
    current?: HierarchyLogOperationNodeDump,
    setters?: HierarchyLogOperationSetter[]
}

class HierarchiesService {
    fetchHierarchies(workspaceId: string) {
        return axios.get<HierarchyModel[]>(API_URL + `/hierarchies?workspace_id=${workspaceId}`, {headers: authHeader()});
    }

    addHierarchy(hierarchy: string) {
        return axios.post(API_URL + `/hierarchies`, hierarchy, {headers: authHeader()});
    }

    fetchHierarchyById(hierarchyId: string) {
        return axios.get(API_URL + `/hierarchies/${hierarchyId}`, {headers: authHeader()});
    }

    deleteHierarchy(hierarchyId: string) {
        return axios.delete(API_URL + `/hierarchies/${hierarchyId}`, {headers: authHeader()});
    }

    renameHierarchy(hierarchyId: string, newName: string) {
        return axios.put(API_URL + `/hierarchies/${hierarchyId}/name`, {name: newName}, {headers: authHeader()});
    }

    hideHierarchy(hierarchyId: string, hidden: boolean) {
        return axios.post(API_URL + `/hierarchies/${hierarchyId}/hidden`,
        { hidden: hidden },
        { headers: authHeader() });
    }

    addHierarchyNode(hierarchy: Hierarchy, node: HierarchyNode) {
        if (!node.isValid()) return null;
        return axios.post(API_URL + `/hierarchies/${hierarchy.id}/nodes`, node.getDict(), {headers: authHeader()});
    }

    addPageToHierarchyScope(hierarchy: Hierarchy, pageId: string) {
        return axios.post(
            API_URL + `/hierarchies/${hierarchy.id}/pages_in_scope`,
            {},
            {headers: authHeader(), params: {page_id: pageId}}
        );
    }

    setUseRefsV2(hierarchy: Hierarchy, value: boolean) {
        return axios.post(
            API_URL + `/hierarchies/${hierarchy.id}/use_v2_references`,
            {},
            {headers: authHeader(), params: {value: value}}
        );
    }

    setUniqueAttributeKeys(hierarchyId: string, uniqueAttributeKeys: string[]) {
        return axios.post(
            API_URL + `/hierarchies/${hierarchyId}/unique_attribute_keys`,
            { unique_attribute_keys: uniqueAttributeKeys },
            { headers: authHeader() }
        );
    }

    removePageFromHierarchyScope(hierarchy: Hierarchy, pageId: string) {
        return axios.delete(
            API_URL + `/hierarchies/${hierarchy.id}/pages_in_scope`,
            {headers: authHeader(), params: {page_id: pageId}}
        );
    }

    updateHierarchyNode(hierarchy: Hierarchy, node: HierarchyNode) {
        if (!node.isValid()) return Promise.reject();
        const nodeDict = node.getDict();
        return this.updateHierarchyNodeRaw(hierarchy.id, {
            ...nodeDict,
            id: nodeDict.id!,
            references: nodeDict.references.map(x => ({
                ...x,
                id: x.id!
            }))
        });
    }

    updateHierarchyNodeRaw(hierarchyId: string, node: NodeUpdateModel) {
        return axios.put<NodeModel>(API_URL + `/hierarchies/${hierarchyId}/nodes/${node.id}`, node, {headers: authHeader()});
    }

    getNodeUniquenessViolatingAttributes(
        hierarchyId: string,
        nodeId: string
    ): Promise<{ uniqueness_violating_attributes: { key: string; value: string }[] }> {
        return axios
            .get(API_URL + `/hierarchies/${hierarchyId}/nodes/${nodeId}/uniqueness_violating_attributes`, {
                headers: authHeader(),
            })
            .then((res) => res.data);
    }

    addMultipleNodes(hierarchy: Hierarchy, commands: AddMultipleNodeCommand[]) {
        if (commands.some(x => !x.node.isValid())) return Promise.reject();
        return axios.post(
            API_URL + `/hierarchies/${hierarchy.id}/multiple_nodes/`,
            {
                commands: commands.map(x => ({
                    type: x.type,
                    data: x.node.getDict()
                })),
            },
            {headers: authHeader()}
        );
    }

    deleteHierarchyNode(hierarchy: Hierarchy, node: HierarchyNode) {
        return axios.delete(API_URL + `/hierarchies/${hierarchy.id}/nodes/${node.id}`, {headers: authHeader()});
    }

    exportHierarchyToExcel(hierarchy: Hierarchy) {
        axios.post(`${API_URL}/hierarchies/${hierarchy.id}/export_to_xlsx`,
            {}, {
                headers: authHeader(),
                responseType: "blob"
            }).then((response) => {
            const blob = new window.Blob([response.data], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
            download(blob, "hierarchy_exported.xlsx");
        }).catch(() => {
            message.error("Failed to export hierarchy");
        });
    }

    getHierarchyNodeReferenceTagReference(
        hierarchy: Hierarchy | string,
        node: HierarchyNode | string,
        reference: ObjectReference | string
    ) {
        const hierarchyId = isString(hierarchy) ? hierarchy : hierarchy.id;
        const nodeId = isString(node) ? node : node.id;
        const referenceId = isString(reference) ? reference : reference.id;
        return axios.get<TagReference>(
            `${API_URL}/hierarchies/${hierarchyId}/nodes/${nodeId}/references/${referenceId}/tag_reference`,
            {headers: authHeader()}
        );
    }

    getHierarchyNodeReferencesV2First(hierarchy: Hierarchy, node: HierarchyNode) {
        return axios.get<TagReference | null>(
            `${API_URL}/hierarchies/${hierarchy.id}/nodes/${node.id}/references_v2/first`,
            {headers: authHeader()}
        );
    }

    searchInHierarchy(hierarchy: Hierarchy, query: string, page: number, limit: number, filters: SearchInHierarchyFilter[] = []) {
        return axios.post(
            `${API_URL}/hierarchies/${hierarchy.id}/nodes/filtered?query=${query}&page=${page}&limit=${limit}`,
            {filters: filters.filter(x => x?.toRequestBody).map(x => x.toRequestBody!())},
            {headers: authHeader()}
        );
    }

    fetchPartialHierarchyById(hierarchyId: string, tagsList: TagListItem[]) {
        return axios.post<FetchPartialHierarchyResponse>(
            API_URL + `/hierarchies/${hierarchyId}/partial_loading/load_nodes`,
            {tags_list: tagsList}, {headers: authHeader()});
    }

    hierarchyIsInScopeOf(hierarchyId: string, pageId: string) {
        return axios.get(
            API_URL + `/hierarchies/${hierarchyId}/is_in_scope`,
            {params: {page_id: pageId}, headers: authHeader()}
        ).then(response => response.data.result as boolean);
    }

    fetchNodeReferencesExtraData(hierarchy: Hierarchy, node: HierarchyNode) {
        return axios.get<ReferenceExtraData[]>(API_URL + `/hierarchies/${hierarchy.id}/nodes/${node.id}/references_extra_data`, {headers: authHeader()});
    }

    fetchNodeReferencesExtraDataV2(
        hierarchy: Hierarchy,
        node: HierarchyNode,
        page: number,
        limit: number,
        resultReferenceToSkip: ResultReference | undefined = undefined
    ) {
        return axios.post<ReferenceExtraDataV2[]>(
            API_URL + `/hierarchies/${hierarchy.id}/nodes/${node.id}/references_v2/extra_data`,
            {result_reference_to_skip: resultReferenceToSkip},
            {
                headers: authHeader(),
                params: {page, limit}
            }
        );
    }

    fetchSystemAttributes(hierarchyId: string): Promise<HierarchySystemAttributes[]> {
        return axios.get<SystemAttribute[]>(
            API_URL + `/hierarchies/${hierarchyId}/system_attributes`,
            {headers: authHeader()}
        ).then(({data}) =>
            data.map((x) => ({
                name: x.attribute_name,
                options: x.values_list,
                label: x.object_label
            }))
        );
    }

    getPossibleSearchOptionsUrl(hierarchyId: string) {
        return `${API_URL}/hierarchies/${hierarchyId}/nodes/possible_search_option_values`;
    }

    getHierarchyLogsByCurrentUser(hierarchyId: string, page: number, limit: number, showAll: boolean) {
        return axios.get<HierarchyLog[]>(
            API_URL + `/hierarchies/${hierarchyId}/logs/for_user`,
            {headers: authHeader(), params: {page, limit, show_all: showAll}}
        );
    }

    getHierarchyLogOperations(hierarchyId: string, logId: string, page: number, limit: number) {
        return axios.get<HierarchyLogOperation[]>(
            API_URL + `/hierarchies/${hierarchyId}/logs/${logId}/operations`,
            {headers: authHeader(), params: {page, limit}}
        );
    }

    getHierarchyLog(hierarchyId: string, logId: string) {
        return axios.get<HierarchyLog>(
            API_URL + `/hierarchies/${hierarchyId}/logs/${logId}`,
            {headers: authHeader()}
        );
    }

    revertHierarchyLog(hierarchyId: string, logId: string) {
        return axios.put<HierarchyLog[]>(
            API_URL + `/hierarchies/${hierarchyId}/logs/${logId}/revert`,
            {headers: authHeader()}
        );
    }

    async expandSubtree(hierarchyId: string, nodeId: string): Promise<ExpandedSubtreeNode[]> {
        const resp = await axios.get<ExpandedSubtreeNode[]>(
            `${API_URL}/hierarchies/${hierarchyId}/nodes/${nodeId}/expanded_subtree_ids`,
            {headers: authHeader()}
        );
        return resp.data;
    }

    async bulkUpdateHierarchyFromScriptPreview(
        hierarchyId: string,
        templateId: string,
        userData: any,
        context: any
    ): Promise<string> {
        const response = await axios.post<string>(
            API_URL + `/hierarchies/${hierarchyId}/scriptable_bulk_update_preview`,
            {
                template_id: templateId,
                user_data: userData,
                context: context
            },
            {headers: authHeader()}
        );
        return response.data;
    }

    async getBulkUpdateHierarchyFromScriptPreviewResult(
        hierarchyId: string,
        taskId: string
    ): Promise<any> {
        const response = await axios.get(
            API_URL + `/hierarchies/${hierarchyId}/scriptable_bulk_update_preview/${taskId}/result`,
            {headers: authHeader()}
        );
        return response.data;
    }

    async getBulkUpdateHierarchyFromScriptPreviewProgress(
        hierarchyId: string,
        taskId: string
    ): Promise<any> {
        const response = await axios.get(
            API_URL + `/hierarchies/${hierarchyId}/scriptable_bulk_update_preview/${taskId}/progress`,
            {headers: authHeader()}
        );
        return response.data;
    }

    async bulkUpdateHierarchyFromJson(
        hierarchyId: string,
        commands: any
    ): Promise<string> {
        const response = await axios.post<string>(
            API_URL + `/hierarchies/${hierarchyId}/bulk_update_from_json`,
            {
                commands: commands,
            },
            {headers: authHeader()}
        );
        return response.data;
    }

    async getBulkUpdateHierarchyFromJsonProgress(
        hierarchyId: string,
        taskId: string
    ): Promise<any> {
        const response = await axios.get(
            API_URL + `/hierarchies/${hierarchyId}/bulk_update_from_json/${taskId}/progress`,
            {headers: authHeader()}
        );
        return response.data;
    }

    async getBulkUpdateHierarchyFromJsonResult(
        hierarchyId: string,
        taskId: string
    ): Promise<{log_id: string}> {
        const response = await axios.get(
            API_URL + `/hierarchies/${hierarchyId}/bulk_update_from_json/${taskId}/result`,
            {headers: authHeader()}
        );
        return response.data;
    }

    getImportProgress(hierarchyId: string, taskId: any) {
        return axios.get(
            `${API_URL}/hierarchies/${hierarchyId}/import_sub_hierarchy/${taskId}/progress`,
            {headers: authHeader()}
        );
    }

    importSubHierarchy(hierarchyId: string, sourceHierarchyId: string, sourceNodeId: string, targetNodeId: string) {
        return axios.post(
            `${API_URL}/hierarchies/${hierarchyId}/import_sub_hierarchy`,
            {
                source_hierarchy_id: sourceHierarchyId,
                source_node_id: sourceNodeId,
                target_node_id: targetNodeId,
            },
            { headers: authHeader() }
        );
    }
}

export default new HierarchiesService();
